import { useEffect, useState, useCallback } from "react";
import clsx from "clsx";
import { Link } from "react-router";
import TopBarProgress from "react-topbar-progress-indicator";
import { Button } from "@/components/ui/button";
import { useGetConversationsQuery, useUpdateConversationMutation } from "@/api/open-chat";
import { OpenChatMessage } from "@/types/open-chat";
import { PanelType } from "../types";

interface Props {
  isPanelOpen: boolean;
  onPanelToggle: (panel: Exclude<PanelType, null>) => void;
  conversationId: string;
  messages: OpenChatMessage[];
}

export const ChatHeader = ({ isPanelOpen, onPanelToggle, conversationId, messages }: Props) => {
  const [updateConversation, { isLoading }] = useUpdateConversationMutation();
  const { data: conversations, isSuccess } = useGetConversationsQuery();
  const conversation = conversations?.find(c => c.id === conversationId);
  const [name, setName] = useState<string>("Open Chat by Pathfinder");

  const updateConversationName = useCallback(
    async (newName: string) => {
      const response = await updateConversation({ id: conversationId, data: { name: newName.trim() } }).unwrap();
      setName(response.name);
    },
    [updateConversation, conversationId],
  );

  useEffect(() => {
    if (isSuccess && conversation?.name) {
      setName(conversation?.name);
    }
  }, [isSuccess, conversation]);

  const generateAutoTitle = messages.filter(row => row.type === "ai" && row.completed).length === 1;

  useEffect(() => {
    if (generateAutoTitle && conversation && !conversation.name) {
      updateConversationName("");
    }
  }, [generateAutoTitle, conversation, updateConversationName]);

  return (
    <header className="flex items-center justify-between border-b border-input px-6 py-4">
      {isLoading && <TopBarProgress />}

      <div className="flex min-w-0 flex-1 items-center gap-2">
        <img src="/images/pathfinder_logo_beta.svg" className="h-6 flex-shrink-0" />
        {isSuccess && (
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            onBlur={() => updateConversationName(name)}
            className="min-w-0 flex-1 border-none bg-transparent py-0 text-sm focus:outline-none"
          />
        )}
      </div>

      <div className="flex items-center gap-2">
        <div
          className={clsx("flex items-center gap-2 overflow-hidden transition-all duration-500 ease-in-out", {
            "w-0 opacity-0": isPanelOpen,
            "w-auto opacity-100": !isPanelOpen,
          })}
        >
          <Button variant="light-beige-secondary" size="sm" onClick={() => onPanelToggle("improve")}>
            Improve
          </Button>
          <Button variant="light-beige-secondary" size="sm" onClick={() => onPanelToggle("library")}>
            Library
          </Button>
          <Button variant="light-beige-secondary" size="sm" onClick={() => onPanelToggle("history")}>
            History
          </Button>
        </div>
        <Link to="/open-chat">
          <Button variant="default" size="sm">
            New Chat
          </Button>
        </Link>
      </div>
    </header>
  );
};
